"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { LogIn, AlertCircle } from "lucide-react";

export default function LoginPage() {
  const router = useRouter();
  const params = useSearchParams();
  const next = params.get("next") || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setErr(json.error ?? "Credenciales inválidas");
        return;
      }
      router.push(next);
      router.refresh();
    } catch (e: any) {
      setErr(e.message ?? "Error de red");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FDF2F8] to-[#F3E8FF] p-6">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-6">
          <div className="inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br from-[#CD0985] to-[#8500BC] text-white items-center justify-center font-black text-xl tracking-tight shadow-[0_10px_28px_rgba(205,9,133,0.3)] mb-3">
            H2A
          </div>
          <h1 className="font-sans text-2xl font-black tracking-tight text-[#111]">Krediya Reactiva</h1>
          <p className="font-display text-[12px] text-[#6B7280] mt-1">Ingresá con tu cuenta de operador</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-[16px] p-6 shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-[#E5E7EB] space-y-4"
        >
          <div>
            <Label>Email</Label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="sergio@h2a.studio"
              className="w-full px-3.5 py-2.5 bg-white border-[1.5px] border-[#E5E7EB] rounded-[10px] text-[13px] focus:border-[#CD0985] focus:outline-none focus:ring-4 focus:ring-[#CD0985]/10"
            />
          </div>
          <div>
            <Label>Contraseña</Label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3.5 py-2.5 bg-white border-[1.5px] border-[#E5E7EB] rounded-[10px] text-[13px] focus:border-[#CD0985] focus:outline-none focus:ring-4 focus:ring-[#CD0985]/10"
            />
          </div>

          {err && (
            <div className="px-3 py-2 bg-[#FEE2E2] border border-[#FCA5A5] rounded-[8px] text-[12px] text-[#991B1B] font-display inline-flex items-start gap-1.5">
              <AlertCircle size={14} strokeWidth={2.5} className="flex-shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !email || !password}
            className="w-full px-4 py-3 bg-[#CD0985] hover:bg-[#9F0668] disabled:opacity-50 text-white rounded-[10px] font-display font-bold text-[13px] inline-flex items-center justify-center gap-2 shadow-[0_4px_14px_rgba(205,9,133,0.35)]"
          >
            <LogIn size={15} strokeWidth={2.5} />
            {busy ? "Entrando..." : "Entrar"}
          </button>
        </form>

        <p className="text-center font-display text-[10.5px] text-[#9CA3AF] mt-4">
          H2A Studio · Krediya Reactiva
        </p>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-display text-[10.5px] tracking-[0.16em] uppercase text-[#374151] font-bold mb-1.5">
      {children}
    </div>
  );
}
